// frontend/src/pages/Blog.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  Calendar, 
  User, 
  Eye, 
  Heart, 
  MessageCircle, 
  Clock,
  X,
  BookOpen,
  TrendingUp,
  Clock as ClockIcon,
  ArrowLeft
} from 'lucide-react';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const sort = searchParams.get('sort') || '-published_at';

  useEffect(() => {
    
    setSearchQuery(search || '');
    fetchBlogData();
    
  }, [category, search, sort]);

  const fetchBlogData = async () => {
    try {
      // دریافت پست‌ها
      const postsParams = {};
      if (category) postsParams.category = category;
      if (search) postsParams.search = search;
      if (sort) postsParams.sort = sort;
      
      const postsResponse = await apiService.getPosts(postsParams);
      setPosts(postsResponse.data.results || postsResponse.data);

      // دریافت دسته‌بندی‌ها
      const categoriesResponse = await apiService.getBlogCategories();
      setCategories(categoriesResponse.data.results || categoriesResponse.data);

      // دریافت پست‌های پربازدید
      const popularResponse = await apiService.getPosts({
        sort: '-view_count',
        limit: 5
      });
      setPopularPosts(popularResponse.data.results || popularResponse.data.slice(0, 5));

    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    } else {
      params.delete('search');
    }
    
    setSearchParams(params);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };

  const handleSortChange = (newSort) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', newSort);
    setSearchParams(params);
  };

  const handleCategoryChange = (categorySlug) => {
    const params = new URLSearchParams(searchParams);
    
    if (categorySlug) {
      params.set('category', categorySlug);
    } else {
      params.delete('category');
    }
    
    setSearchParams(params);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const toggleLike = (postId) => {
    const newLikedPosts = new Set(likedPosts);
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId);
    } else {
      newLikedPosts.add(postId);
    }
    setLikedPosts(newLikedPosts);
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'اخیراً';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'دیروز';
    if (diffDays < 7) return `${diffDays} روز پیش`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} هفته پیش`;
    return `${Math.ceil(diffDays / 30)} ماه پیش`;
  };

  const calculateReadTime = (content) => {
    if (!content) return '۵';
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return minutes;
  };

  const getSortOptions = () => [
    { value: '-published_at', label: 'جدیدترین', icon: ClockIcon },
    { value: 'published_at', label: 'قدیمی‌ترین', icon: ClockIcon },
    { value: '-view_count', label: 'پربازدیدترین', icon: TrendingUp },
    { value: 'view_count', label: 'کم‌بازدیدترین', icon: TrendingUp },
    { value: 'title', label: 'الفبایی (صعودی)', icon: BookOpen },
    { value: '-title', label: 'الفبایی (نزولی)', icon: BookOpen },
  ];

  const getActiveFiltersCount = () => {
    let count = 0;
    if (category) count++;
    if (search) count++;
    return count;
  };

  const getSEOTitle = () => {
    if (search) {
      return `نتایج جستجو برای "${search}" - بلاگ BusinessPro`;
    }
    if (category) {
      const categoryName = categories.find(c => c.slug === category)?.name;
      return `${categoryName} - مقالات BusinessPro`;
    }
    return 'بلاگ تخصصی صنعت - BusinessPro';
  };

  const getSEODescription = () => {
    if (search) {
      return `جستجو و مطالعه مقالات مرتبط با "${search}" در بلاگ تخصصی BusinessPro.`;
    }
    if (category) {
      const categoryName = categories.find(c => c.slug === category)?.name;
      return `مطالعه مقالات تخصصی ${categoryName} در بلاگ BusinessPro.`;
    }
    return 'بلاگ تخصصی BusinessPro - مقالات آموزشی، اخبار صنعت و مطالب تخصصی در زمینه صنعت و تولید.';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={getSEOTitle()}
        description={getSEODescription()}
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl('/static/images/og-blog.jpg')}
        type="blog"
      />
      
      <div className="min-h-screen bg-black py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12 fade-in">
            <h1 className="text-4xl font-bold text-white mb-4">📝 بلاگ تکنو پلیمر اسپادانا</h1>
            <p className="text-gray-400 text-lg">آخرین مقالات، اخبار و مطالب آموزشی صنعت</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Search Box */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <h3 className="font-bold text-white mb-4 flex items-center space-x-2 space-x-reverse">
                  <Search size={20} />
                  <span>جستجو در مقالات</span>
                </h3>
                <form onSubmit={handleSearch}>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="جستجو..."
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  </div>
                </form>
              </div>

              {/* Categories */}
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                <h3 className="font-bold text-white mb-4 flex items-center space-x-2 space-x-reverse">
                  <Filter size={20} />
                  <span>دسته‌بندی‌ها</span>
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange(null)}
                    className={`w-full text-right px-4 py-3 rounded-lg transition-all ${
                      !category 
                        ? 'bg-yellow-400 text-gray-900 font-bold' 
                        : 'text-gray-300 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    همه مقالات
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-right px-4 py-3 rounded-lg transition-all flex items-center justify-between ${
                        category === cat.slug
                          ? 'bg-yellow-400 text-gray-900 font-bold'
                          : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs bg-gray-700 px-2 py-1 rounded">
                        ({cat.posts_count})
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Popular Posts */}
              {popularPosts.length > 0 && (
                <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
                  <h3 className="font-bold text-white mb-4 flex items-center space-x-2 space-x-reverse">
                    <TrendingUp size={20} />
                    <span>پربازدیدترین‌ها</span>
                  </h3>
                  <div className="space-y-4">
                    {popularPosts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="flex space-x-3 space-x-reverse group"
                      >
                        {post.featured_image && (
                          <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                            <img
                              src={getFullImageUrl(post.featured_image)}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white text-sm font-medium line-clamp-2 group-hover:text-yellow-400 transition-colors">
                            {post.title}
                          </h4>
                          <div className="flex items-center space-x-2 space-x-reverse text-gray-400 text-xs mt-1">
                            <Eye size={12} />
                            <span>{post.view_count || 0}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Subscription */}
              <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl p-6">
                <h3 className="font-bold text-gray-900 mb-3">عضویت در خبرنامه</h3>
                <p className="text-gray-800 text-sm mb-4">
                  از آخرین مقالات و اخبار صنعت مطلع شوید
                </p>
                <form className="space-y-3">
                  <input
                    type="email"
                    placeholder="ایمیل شما"
                    className="w-full px-4 py-2 rounded-lg bg-white bg-opacity-90 text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                  <button
                    type="submit"
                    className="w-full bg-gray-900 text-white py-2 rounded-lg font-bold hover:bg-gray-800 transition-colors"
                  >
                    عضویت
                  </button>
                </form>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Filters Bar */}
              <div className="bg-gray-900 rounded-2xl p-6 mb-8 border border-gray-800">
                <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                  <div className="flex items-center space-x-4 space-x-reverse">
                    {/* Results Count */}
                    <div className="text-gray-300">
                      <span className="font-medium">{posts.length}</span> مقاله یافت شد
                      {getActiveFiltersCount() > 0 && (
                        <span className="text-gray-400 text-sm mr-2">
                          ({getActiveFiltersCount()} فیلتر فعال)
                        </span>
                      )}
                    </div>

                    {/* Active Filters */}
                    {(search || category) && (
                      <div className="flex items-center space-x-2 space-x-reverse">
                        {search && (
                          <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm flex items-center space-x-1 space-x-reverse">
                            <span>جستجو: {search}</span>
                            <button
                              onClick={() => {
                                const params = new URLSearchParams(searchParams);
                                params.delete('search');
                                setSearchParams(params);
                              }}
                              className="hover:text-gray-700"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        )}
                        {category && (
                          <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm flex items-center space-x-1 space-x-reverse">
                            <span>دسته‌بندی: {categories.find(c => c.slug === category)?.name}</span>
                            <button
                              onClick={() => {
                                const params = new URLSearchParams(searchParams);
                                params.delete('category');
                                setSearchParams(params);
                              }}
                              className="hover:text-gray-700"
                            >
                              <X size={14} />
                            </button>
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-4 space-x-reverse">
                    {/* Sort Options */}
                    <select
                      value={sort}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all text-sm"
                    >
                      {getSortOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {/* View Mode Toggle */}
                    <div className="flex items-center space-x-1 space-x-reverse bg-gray-800 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'grid' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                        }`}
                      >
                        <Grid size={18} />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'list' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                        }`}
                      >
                        <List size={18} />
                      </button>
                    </div>

                    {/* Clear Filters */}
                    {getActiveFiltersCount() > 0 && (
                      <button
                        onClick={clearAllFilters}
                        className="text-gray-400 hover:text-white transition-colors text-sm flex items-center space-x-1 space-x-reverse"
                      >
                        <X size={16} />
                        <span>پاک کردن فیلترها</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Posts Grid/List */}
              {posts.length === 0 ? (
                <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
                  <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-white mb-2">
                    {search || category ? 'مقاله‌ای یافت نشد' : 'هنوز مقاله‌ای منتشر نشده'}
                  </h3>
                  <p className="text-gray-400 mb-6">
                    {search || category 
                      ? 'لطفاً عبارت جستجو یا فیلترها را تغییر دهید.' 
                      : 'به زودی مقالات جدید منتشر خواهد شد.'}
                  </p>
                  {(search || category) && (
                    <button
                      onClick={clearAllFilters}
                      className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
                    >
                      نمایش همه مقالات
                    </button>
                  )}
                </div>
              ) : (
                <div className={
                  viewMode === 'grid' 
                    ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
                    : 'space-y-6'
                }>
                  {posts.map((post) => (
                    <PostCard 
                      key={post.id} 
                      post={post} 
                      viewMode={viewMode} 
                      liked={likedPosts.has(post.id)}
                      onLike={() => toggleLike(post.id)}
                      timeAgo={getTimeAgo(post.published_at)}
                      readTime={calculateReadTime(post.content)}
                    />
                  ))}
                </div>
              )}

              {/* Load More Button (برای صفحه‌بندی آینده) */}
              {posts.length > 0 && (
                <div className="text-center mt-8">
                  <button className="border-2 border-yellow-400 text-yellow-400 px-8 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all">
                    بارگذاری مقالات بیشتر
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// کامپوننت کارت پست
const PostCard = ({ post, viewMode, liked, onLike, timeAgo, readTime }) => {
  if (viewMode === 'list') {
    return (
      <article className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
        <div className="flex flex-col md:flex-row">
          {/* Image */}
          <div className="md:w-64 flex-shrink-0 relative">
            <img
              src={getFullImageUrl(post.featured_image)}
              alt={post.title}
              className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
              {post.category_name}
            </div>
            <button
              onClick={onLike}
              className={`absolute top-4 right-4 p-2 rounded-full bg-black bg-opacity-50 transition-colors ${
                liked ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
              }`}
            >
              <Heart fill={liked ? 'currentColor' : 'none'} size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {post.author_name} • {timeAgo}
                </p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-4 line-clamp-3">
              {post.excerpt}
            </p>

            {/* Stats and Actions */}
            <div className="flex items-center justify-between border-t border-gray-800 pt-4">
              <div className="flex items-center space-x-4 space-x-reverse text-gray-400 text-sm">
                <div className="flex items-center space-x-1 space-x-reverse">
                  <Eye size={16} />
                  <span>{post.view_count || 0}</span>
                </div>
                <div className="flex items-center space-x-1 space-x-reverse">
                  <MessageCircle size={16} />
                  <span>{post.comment_count || 0}</span>
                </div>
                <div className="flex items-center space-x-1 space-x-reverse">
                  <Clock size={16} />
                  <span>{readTime} دقیقه</span>
                </div>
              </div>
              
              <Link
                to={`/blog/${post.slug}`}
                className="text-yellow-400 hover:text-yellow-300 font-medium flex items-center space-x-1 space-x-reverse"
              >
                <span>مطالعه مقاله</span>
                <ArrowLeft size={16} />
              </Link>
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Grid View
  return (
    <article className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={getFullImageUrl(post.featured_image)}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-40"></div>
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
          {post.category_name}
        </div>
        
        {/* Like Button */}
        <button
          onClick={onLike}
          className={`absolute top-4 right-4 p-2 rounded-full bg-black bg-opacity-50 transition-colors ${
            liked ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
          }`}
        >
          <Heart fill={liked ? 'currentColor' : 'none'} size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-center justify-between text-gray-400 text-sm mb-3">
          <div className="flex items-center space-x-2 space-x-reverse">
            <User size={14} />
            <span>{post.author_name}</span>
          </div>
          <div className="flex items-center space-x-1 space-x-reverse">
            <Calendar size={14} />
            <span>{timeAgo}</span>
          </div>
        </div>

        <h3 className="text-lg font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors line-clamp-2">
          {post.title}
        </h3>

        <p className="text-gray-300 text-sm mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between text-gray-400 text-sm border-t border-gray-800 pt-4">
          <div className="flex items-center space-x-3 space-x-reverse">
            <div className="flex items-center space-x-1 space-x-reverse">
              <Eye size={14} />
              <span>{post.view_count || 0}</span>
            </div>
            <div className="flex items-center space-x-1 space-x-reverse">
              <MessageCircle size={14} />
              <span>{post.comment_count || 0}</span>
            </div>
            <div className="flex items-center space-x-1 space-x-reverse">
              <Clock size={14} />
              <span>{readTime} دقیقه</span>
            </div>
          </div>
          
          <Link
            to={`/blog/${post.slug}`}
            className="text-yellow-400 hover:text-yellow-300 text-sm font-medium flex items-center space-x-1 space-x-reverse"
          >
            <span>مطالعه</span>
            <ArrowLeft size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default Blog;